import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { Alert, Box, Snackbar } from "@mui/material";
import { cloneDeep, isNil } from "lodash";
import { DateTime } from "luxon";
import { usePostHog } from "posthog-js/react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { validate } from "validate.js";

import {
  createInitialFormState,
  createShipmentFormDraftContext,
} from "context/createShipmentFormDraftContext";
import {
  useAPI,
  useCarrierExists,
  useDisclosure,
  useRoleContext,
  useShipperNetwork,
  useSubscriptionStatus,
  useSupplierCustomers,
} from "hooks";
import { useApiService } from "hooks/useApiService";
import { useAuthContext } from "hooks/useAuthContext";
import {
  AVAILABLE_LOADS_ERROR_MESSAGE,
  availableLoadsAreValid,
  DEFAULT_ASSIGNMENT_CURRENCY,
  emailIsValid,
  getApiErrorMessage,
  getRegionForCoordinates,
  isBookNowEligibleShipmentType,
  isFreeTierShipperSubscription,
  isInvalidNumber,
  shipperSubscriptionTierWithoutPrivateShipments,
  uploadFilesToS3,
} from "utils";

import { APIErrorCode, ClientAppEvent } from "../../../../constants";
import { CreateShipmentTemplateButton } from "../CreateShipmentTemplateButton";
import { MissingPostingPermissionModal } from "./components/MissingPostingPermissionModal";
import { RoutePoint } from "./components/RoutePoint";
import { RoutesForm } from "./components/RoutesForm";
import { RoutesFormModal } from "./components/RoutesFormModal";
import { createShipmentRequestBodyConstraints } from "./constraints";
import { CreateShipmentForm } from "./CreateShipmentForm";
import {
  areRouteTimesValid,
  DIRECT_BOOKING_MESSAGE,
  getAssigneeCheckedEventOrNone,
  getDirectBookingEquipmentError,
  getDirectBookingErrorMessage,
  getDirectBookingShipmentTypeError,
  getDirectBookingTemplateError,
  getVisibilityBody,
  parseNumericFormInput,
  redactEmails,
  validateDirectBookingAssignment,
} from "./utils";

import type {
  CreateMultiShipmentProjectBody,
  CreateShipmentBody,
  CreateShipmentFormState,
  CreateShipmentRouteBody,
  CreateShipmentShipmentBody,
  DirectBookingAssignmentFormState,
} from "types/shipment";

import type { UnifiedPartnerSelectorRef } from "./components/ConsolidatedShipmentVisibility/components/UnifiedPartnerSelector";
import type { DirectBookingAssigneeCheck } from "./utils";

interface UseCreateShipmentFormStateProps {
  shipmentStateProp: CreateShipmentFormState | undefined;
}

interface UseCreateShipmentFormStateResult {
  formState: CreateShipmentFormState;
  setFormState: React.Dispatch<React.SetStateAction<CreateShipmentFormState>>;
  resetFormState: () => void;
}

function useCreateShipmentFormState(
  props: UseCreateShipmentFormStateProps,
): UseCreateShipmentFormStateResult {
  const { shipmentStateProp } = props;
  const draftContext = useContext(createShipmentFormDraftContext);
  const [localFormState, setLocalFormState] = useState<CreateShipmentFormState>(
    createInitialFormState,
  );

  const localResetFormState = useCallback(
    () => setLocalFormState(createInitialFormState()),
    [],
  );

  const formState =
    draftContext !== null ? draftContext.formState : localFormState;
  const setFormState =
    draftContext !== null ? draftContext.setFormState : setLocalFormState;
  const resetFormState =
    draftContext !== null ? draftContext.resetFormState : localResetFormState;

  useEffect(() => {
    if (shipmentStateProp !== undefined) {
      setFormState(cloneDeep(shipmentStateProp));
    }
  }, [shipmentStateProp, setFormState]);

  return { formState, setFormState, resetFormState };
}

interface Props {
  shipmentState?: CreateShipmentFormState;
  error?: string | null;
  postShipmentUrl: string;
  onPreSubmitShipment?: (body: any) => void;
  submitShipment?: boolean;
  showCreateTemplate?: boolean;
  showBookNow?: boolean;
  onSubmitShipmentSuccessCallback?: VoidFunction | null;
  showShareMenu?: boolean;
}

export function CreateShipmentFormContainer(props: Props) {
  const {
    shipmentState,
    error: copyError = null,
    postShipmentUrl,
    onPreSubmitShipment = null,
    submitShipment = true,
    showCreateTemplate = true,
    showBookNow = true,
    onSubmitShipmentSuccessCallback = null,
    showShareMenu = false,
  } = props;
  const { formState, setFormState, resetFormState } =
    useCreateShipmentFormState({ shipmentStateProp: shipmentState });
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    isOpen: shipmentPostedPromptOpen,
    onClose: onCloseShipmentPostedDialog,
    onOpen: onOpenShipmentPostedDialog,
  } = useDisclosure(false);
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const [error, setError] = useState<string | null>(copyError ?? null);
  // The direct booking section shows its errors inline once this is set.
  const [reviewAttempted, setReviewAttempted] = useState<boolean>(false);
  const { api } = useAPI();
  const { user } = useAuthContext();
  useApiService();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const { data: supplierCustomers } = useSupplierCustomers();
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [
    showMissingPostingPermissionModal,
    setShowMissingPostingPermissionModal,
  ] = useState<boolean>(false);
  const [emailInputRef, setEmailInputRef] =
    useState<UnifiedPartnerSelectorRef | null>(null);
  const { subscription } = useSubscriptionStatus();
  const isFreeTier = isFreeTierShipperSubscription(subscription);
  const assignment = formState.directBookingAssignment;
  const canUseBookNow =
    subscription != null &&
    !shipperSubscriptionTierWithoutPrivateShipments(subscription) &&
    formState.isBookNow &&
    isBookNowEligibleShipmentType(formState.shipmentType) &&
    assignment === null;

  // The assignee check. SWR shares this fetch with the visibility section, so
  // no second request is made for the network.
  const { followingCarriers, loading: loadingNetwork } = useShipperNetwork();
  const assigneeInNetwork =
    assignment !== null &&
    followingCarriers.some(
      (carrier) =>
        carrier.user.email.toLowerCase() ===
        assignment.carrierEmail.toLowerCase(),
    );
  // Held until the network loads, so an email is never recorded as outside the
  // network before the network is known. A carrier in the network is checked
  // too: blocking a partner does not remove them from the network, so only the
  // check knows they can no longer be booked.
  const { retry: retryCarrierCheck, ...carrierCheck } = useCarrierExists({
    email:
      assignment !== null && !loadingNetwork ? assignment.carrierEmail : null,
  });
  const assigneeCheck: DirectBookingAssigneeCheck = {
    inNetwork: assigneeInNetwork,
    ...carrierCheck,
  };

  const assigneeEmail = assignment?.carrierEmail ?? "";
  const {
    carrierExists,
    rejectionCode,
    isLoading,
    error: checkError,
  } = carrierCheck;
  const lastTrackedAssigneeEmail = useRef<string | null>(null);
  useEffect(() => {
    const event = getAssigneeCheckedEventOrNone({
      assigneeEmail,
      assigneeCheck: {
        inNetwork: assigneeInNetwork,
        carrierExists,
        rejectionCode,
        isLoading,
        error: checkError,
      },
      lastTrackedEmail: lastTrackedAssigneeEmail.current,
    });
    if (event === null) {
      return;
    }
    lastTrackedAssigneeEmail.current = assigneeEmail;
    posthog?.capture(
      ClientAppEvent.CREATE_SHIPMENT_DIRECT_BOOKING_ASSIGNEE_CHECKED,
      event,
    );
  }, [
    assigneeEmail,
    assigneeInNetwork,
    carrierExists,
    rejectionCode,
    isLoading,
    checkError,
    posthog,
  ]);

  const handleOpen = () => {
    onOpen();
  };

  const logStartingTime = () => {
    if (formState.analytics.creationStartingTime === null) {
      setFormState((prev) => ({
        ...prev,
        analytics: {
          ...prev.analytics,
          creationStartingTime: DateTime.now(),
        },
      }));
    }
  };

  const handleError = (navError: string, isAlert = false) => {
    if (!isAlert) {
      setError(navError);
    }
    setFormState((prev) => ({
      ...prev,
      analytics: {
        ...prev.analytics,
        encounteredErrors: [
          ...prev.analytics.encounteredErrors,
          { message: navError, timestamp: DateTime.now().toISO() },
        ],
      },
    }));
    logStartingTime();
  };

  const handleValueChange = <T,>(
    key: keyof CreateShipmentFormState,
    value: T | null,
  ): void => {
    // Clear error when user starts typing and re-validate the specific field
    if (error) {
      // Check if the current error is related to the field being changed
      const updatedFormState = { ...formState, [key]: value };
      // Re-validate specific field to see if error is resolved
      let shouldClearError = false;
      if (key === "weightLb" && error.includes("Weight must be a number")) {
        shouldClearError = !isInvalidNumber(value as unknown as string);
      } else if (
        key === "bookNowPrice" &&
        error.includes("Book Now price must be a number")
      ) {
        shouldClearError = !isInvalidNumber(value as unknown as string);
      } else if (
        key === "availableLoads" &&
        error === AVAILABLE_LOADS_ERROR_MESSAGE
      ) {
        shouldClearError = availableLoadsAreValid(
          value as unknown as number | null,
        );
      } else if (key === "equipmentTypes" && error.includes("equipment")) {
        shouldClearError = (value as unknown as string[])?.length > 0;
      } else if (
        key === "routePoints" &&
        (error.includes("route point") || error.includes("location"))
      ) {
        // For route points, just clear the error as validation is complex
        shouldClearError = true;
      } else if (
        error.includes("commodity") &&
        (key === "commodity" || key === "commodityFreetext")
      ) {
        shouldClearError = !!(
          updatedFormState.commodity || updatedFormState.commodityFreetext
        );
      } else {
        // For other cases, clear the error to provide immediate feedback
        shouldClearError = true;
      }

      if (shouldClearError) {
        setError(null);
      }
    }
    // validation here
    if (key === "routePoints") {
      setFormState((prev) => {
        const rps = [...prev.routePoints];
        rps.splice(
          prev.routePoints.length - 1,
          0,
          value as unknown as EditableRoutePointState,
        );
        const updatedAddressModes: RoutePointAddressModes = {};
        rps.forEach((rp, index) => {
          updatedAddressModes[index] = rp.inputMode;
        });

        return {
          ...prev,
          routePoints: rps,
          analytics: {
            ...prev.analytics,
            routePointAddressModes: updatedAddressModes,
          },
        };
      });
      onClose();
    } else if (key === "equipmentTypes") {
      const equipmentValues = value as unknown as string[];
      setFormState((prev) => ({
        ...prev,
        equipmentTypes: equipmentValues,
      }));
    } else if (key === "bookNowPrice") {
      const price = typeof value === "string" ? value.trim() : "";
      setFormState((prev) => ({
        ...prev,
        bookNowPrice: price,
        isBookNow: price !== "",
      }));
    } else {
      setFormState((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
    logStartingTime();
  };

  const onSetRoutePointBeingEdited = (index: number, edit: boolean) => {
    setFormState((prev) => {
      const rps = [...prev.routePoints];
      rps[index].isBeingEdited = edit;
      return {
        ...prev,
        routePoints: rps,
      };
    });
  };

  const handleRoutePointLocationValueChange = <T,>(
    key: keyof RoutePointLocation,
    value: T | null,
    index: number,
  ): void => {
    setFormState((prev) => {
      const rps = [...prev.routePoints];
      const currentPoint = rps[index];
      const newPoint: EditableRoutePointState = {
        ...currentPoint,
        point_location: {
          ...currentPoint.point_location,
          [key]: value,
        } as RoutePointLocation,
      };
      rps[index] = newPoint;

      return {
        ...prev,
        routePoints: rps,
      };
    });
  };

  const handleRoutePointValueChange = <T,>(
    key: keyof EditableRoutePointState,
    value: T | null,
    index: number,
  ): void => {
    setFormState((prev) => {
      const rps = [...prev.routePoints];
      rps[index] = { ...rps[index], [key]: value };
      return {
        ...prev,
        routePoints: rps,
      };
    });
    /*
    Whenever point location is updated with coordinates (when an autocomplete location is chosen)
    and province is not present (since province is not included by mapbox a lot of times), find the
    province 'manually' using the coordinates.
    */
    if (key === "point_location" && value != null) {
      const location = value as unknown as RoutePointLocation;
      if (
        !isNil(location.longitude) &&
        !isNil(location.latitude) &&
        (location.province === "" || isNil(location.province))
      ) {
        getRegionForCoordinates(location.longitude, location.latitude).then(
          (region) => {
            if (region !== undefined) {
              handleRoutePointLocationValueChange("province", region, index);
            }
          },
        );
      }
    }
  };

  const updateInputModeForRoutePoint = (
    index: number,
    mode: "autocomplete" | "manual",
  ) => {
    setFormState((prev) => {
      const rps = prev.routePoints.map((rp, idx) => {
        if (idx !== index) return rp;
        const updatedRp: EditableRoutePointState = { ...rp, inputMode: mode };
        if (mode === "manual" && updatedRp.point_location !== undefined) {
          updatedRp.point_location = {
            ...updatedRp.point_location,
            longitude: null,
            latitude: null,
          };
        }
        return updatedRp;
      });

      const analytics = {
        ...prev.analytics,
        routePointAddressModes: {
          ...prev.analytics.routePointAddressModes,
          [index]: mode,
        },
      };

      return {
        ...prev,
        routePoints: rps,
        analytics,
      };
    });
  };

  const onDeleteMidRoutePoint = (index: number) => {
    setFormState((prev) => {
      const rps = [...prev.routePoints];
      rps.splice(index, 1);
      return {
        ...prev,
        routePoints: rps,
      };
    });
  };

  const routePointsAreValid = () => {
    const { routePoints } = formState;

    if (routePoints.length < 2) {
      handleError("Please add at least 2 route points to your shipment.");
      return false;
    }

    return routePoints.every((rp, i) => {
      if (rp.start_time === undefined) {
        handleError(`Route point ${i + 1} missing start time.`);
        return false;
      }
      if (!rp.point_location) {
        handleError(
          `Route point ${i + 1} is missing location information. Please enter one.`,
        );
        return false;
      }

      if (areRouteTimesValid(rp.start_time)) {
        handleError(areRouteTimesValid(rp.start_time, i) ?? "");
        return false;
      }

      if (
        !rp.point_location?.country ||
        rp.point_location.country === "" ||
        rp.point_location.country === null
      ) {
        handleError(
          `Route point ${i + 1} is missing country information. Please choose a different location.`,
        );
        return false;
      }

      // points without coordinates MUST have city, province and country.
      if (
        (rp.point_location.latitude === null ||
          rp.point_location?.latitude === undefined ||
          rp.point_location.longitude === null ||
          rp.point_location?.longitude === undefined) &&
        (rp.point_location.city === null ||
          rp.point_location.city === "" ||
          rp.point_location.city === undefined ||
          rp.point_location.province === null ||
          rp.point_location.province === "" ||
          rp.point_location.province === undefined ||
          rp.point_location.country === null ||
          rp.point_location.country === "" ||
          rp.point_location.country === undefined)
      ) {
        handleError(
          `Route point ${i + 1} requires city, province and country. Please add the missing information.`,
        );
        return false;
      }

      return true;
    });
  };

  const additionalNotesAddedIfLTL = () => {
    const { service, additionalDetails } = formState;
    if (service === "LESS_THAN_TRUCK_LOAD" && additionalDetails === "") {
      handleError(
        "Please provide the number of pieces and product dimensions for your LTL shipment in the Dimensions & Notes field above.",
      );
      return false;
    }
    return true;
  };

  const shipmentCommodityIsValid = () => {
    if (formState.commodity === "" && formState.commodityFreetext === "") {
      handleError(
        "Please enter a custom commodity or select one from the commodity list.",
      );
      return false;
    }
    return true;
  };

  const bookNowIsValid = () => {
    if (canUseBookNow) {
      if (formState.bookNowPrice === "") {
        handleError("Please enter a Book Now price.");
        return false;
      }
      if (+formState.bookNowPrice <= 0) {
        handleError("Book Now price must be greater than 0.");
        return false;
      }
    }
    return true;
  };

  // Footage and preferred price have no inputs on this form; their values arrive
  // from saved templates, copies and drafts, so a non-numeric value means stored
  // content is corrupt — surface it instead of silently posting a null.
  const optionalNumericFieldsAreValid = () => {
    if (formState.footage.trim() !== "" && isInvalidNumber(formState.footage)) {
      handleError("Footage must be a number.");
      return false;
    }
    if (
      formState.preferredPrice.trim() !== "" &&
      isInvalidNumber(formState.preferredPrice)
    ) {
      handleError("Preferred price must be a number.");
      return false;
    }
    return true;
  };

  const directBookingIsValid = () => {
    if (assignment === null) {
      return true;
    }
    // A failed check is asked again. It only holds Review for a carrier outside
    // the network: a network carrier stays assignable while its check is down.
    if (assigneeCheck.error !== null) {
      retryCarrierCheck();
      if (!assigneeCheck.inNetwork) {
        handleError(DIRECT_BOOKING_MESSAGE.CARRIER_CHECK_PENDING);
        return false;
      }
    }
    const message = validateDirectBookingAssignment({
      formState,
      assignment,
      assigneeCheck,
    });
    if (message !== null) {
      handleError(message);
      return false;
    }
    return true;
  };

  const partnerSelectionIsValid = () => {
    const {
      notificationSettingsType,
      notificationSetingsEmails,
      partnerGroups,
      networkInviteEmails,
    } = formState;

    // Check if there's unprocessed text in email input field or last typed value
    if (emailInputRef) {
      const inputValue = emailInputRef.getInputValue();
      const lastTypedValue = emailInputRef.getLastTypedValue();

      // Check current input value first
      if (inputValue.trim() && !emailIsValid(inputValue.trim())) {
        handleError("Please fix the invalid email format before submitting.");
        return false;
      }

      // If input is empty but user recently typed something invalid, catch that too
      if (
        !inputValue.trim() &&
        lastTypedValue &&
        !emailIsValid(lastTypedValue)
      ) {
        handleError(
          "You entered an invaid email. Please enter a valid email address",
        );
        return false;
      }
    }

    // Check for invalid email formats in network invite emails
    if (networkInviteEmails && networkInviteEmails.length > 0) {
      const invalidEmails = networkInviteEmails.filter(
        (email) => !emailIsValid(email),
      );
      if (invalidEmails.length > 0) {
        handleError(`Invalid email format: ${invalidEmails.join(", ")}`);
        return false;
      }
    }

    // Only validate for notification types that require specific partner selection
    if (
      notificationSettingsType === "SELECTED_NETWORK_CARRIERS" ||
      notificationSettingsType === "PRIVATE_SHIPMENT"
    ) {
      const hasSelectedPartners =
        notificationSetingsEmails && notificationSetingsEmails.length > 0;
      const hasSelectedGroups = partnerGroups && partnerGroups.length > 0;
      const hasNetworkInviteEmails =
        networkInviteEmails && networkInviteEmails.length > 0;

      // For public shipments (SELECTED_NETWORK_CARRIERS), allow no selection
      // (defaults to all partners)
      // For private shipments (PRIVATE_SHIPMENT), require explicit selection
      const requiresSelection = notificationSettingsType === "PRIVATE_SHIPMENT";

      if (
        requiresSelection &&
        !hasSelectedPartners &&
        !hasSelectedGroups &&
        !hasNetworkInviteEmails
      ) {
        // Deliberately does not name "All my partners": the selector only
        // offers it once a carrier has actually joined this shipper's network.
        handleError(
          "Please select at least one partner or add a carrier email address.",
        );
        return false;
      }
    }

    return true;
  };

  // Replaced by the assignee picker while the shipment is a direct booking.
  const visibilitySelectionIsValid = () =>
    assignment !== null ? directBookingIsValid() : partnerSelectionIsValid();

  // Dims & Notes are not required for a shipment already booked.
  const ltlNotesAreValid = () =>
    assignment !== null || additionalNotesAddedIfLTL();

  const convertPointStartTimeToUTC = (
    point: RoutePoint,
    previousRoutePointStartDate: DateTime,
    isTemplate: boolean,
  ): DateTime => {
    if (isTemplate && !point.start_time) {
      return previousRoutePointStartDate;
    }

    const startTimeAsDate = DateTime.fromISO(point.start_time).startOf("day");
    const utcDate = DateTime.utc().startOf("day");
    const localDate = DateTime.now().startOf("day");

    // Make sure the start time is not in the past when ASAP is selected.
    // Use latest pickup date previously seen
    if (point.schedule_type === "ASAP") {
      return previousRoutePointStartDate;
    }

    // Use UTC date if the start time is supposed to be today, but it shows as in the past
    if (utcDate > localDate) {
      return startTimeAsDate.equals(localDate) ? utcDate : startTimeAsDate;
    }
    return startTimeAsDate;
  };

  const buildRequestBodyRoutePoints = (isTemplate = false) => {
    const { routePoints } = formState;
    const bodyRps: CreateShipmentRouteBody[] = [];
    let previousRoutePointStartDate: DateTime = DateTime.now();
    routePoints.forEach((rp, i) => {
      const { point_location: pointLocation, ...rest } = rp;
      const point = { ...rest } as unknown as RoutePoint;

      const startTimeConvertedToUTC = convertPointStartTimeToUTC(
        point,
        previousRoutePointStartDate,
        isTemplate,
      );
      // previous route
      previousRoutePointStartDate = startTimeConvertedToUTC;

      bodyRps.push({
        point: {
          ...point,
          point_number: i + 1,
          start_time: startTimeConvertedToUTC.toFormat("yyyy-MM-dd"),
          schedule_type: point.schedule_type,
        },
        point_location: pointLocation as RoutePointLocation,
      });
    });
    return bodyRps;
  };

  /*
  The free tier refuses sharees, also on a direct booking. They are
  left out of the body rather than cleared from the form, so a template or copy
  restoring an assignment is covered, and switching back to Private keeps them.
  */
  const getShareeRoleIdsForBody = (): number[] =>
    assignment !== null && isFreeTier ? [] : formState.shareeRoleIds;

  const buildSupplierShipmentInfo = () => {
    const hasCustomers =
      formState.customerRoleIds.length > 0 ||
      formState.externalCustomerIds.length > 0 ||
      formState.customerInviteEmails.length > 0;

    if (!hasCustomers) {
      return null;
    }

    return {
      customer_role_ids: formState.customerRoleIds,
      external_customer_ids: formState.externalCustomerIds,
      customer_invite_emails: formState.customerInviteEmails,
    };
  };

  const buildProjectBody = (): CreateMultiShipmentProjectBody | null => {
    /*
    Only sends project details the API accepts. A blank or out of range loads
    count is left out entirely instead of being sent as 0, which the API rejects
    and which would otherwise be persisted into a shipment template.
    */
    const { availableLoads } = formState;
    if (
      formState.shipmentType !== "MULTI_SHIPMENT_PROJECT" ||
      !availableLoadsAreValid(availableLoads)
    ) {
      return null;
    }
    return { loads_available: availableLoads };
  };

  const getShipmentBody = (isTemplate = false): CreateShipmentBody => {
    // parses the form state and returns a shipment object ready for posting.
    // Does not validate that the shipment form contains all necessary fields or correct values
    let service = null;
    if (formState.service !== "") {
      service = formState.service;
    }
    const shipment = {
      service,
      type: formState.shipmentType,
      weight_lb: formState.weightLb,
      footage: parseNumericFormInput(formState.footage),
      preferred_price: parseNumericFormInput(formState.preferredPrice),
      currency: formState.currency,
      commodity: "",
      special_requirements: formState.specialRequirements,
      additional_details: formState.additionalDetails,
      commodity_freetext: formState.commodityFreetext,
      book_now_currency: canUseBookNow ? formState.bookNowCurrency : null,
      book_now_price: canUseBookNow ? parseFloat(formState.bookNowPrice) : null,
      custom_name: formState.customName === "" ? null : formState.customName,
      quoting_unit_of_measure: formState.quotingUnitOfMeasure,
      analytics: {
        shipment_creation_mode: "TEMPLATE" as ShipmentCreationModeType,
        shipment_creation_start:
          formState.analytics.creationStartingTime?.toISO() ??
          DateTime.now().toISO(),
        shipment_creation_end: DateTime.now().toISO(),
        file_attachments: [],
        encountered_errors: [] as AnalyticsError[],
        route_point_address_modes: formState.analytics.routePointAddressModes,
      },
    };

    const route = buildRequestBodyRoutePoints(isTemplate);
    const equipments = formState.equipmentTypes;

    const createShipmentRequestBody: CreateShipmentBody = {
      shipment,
      route,
      equipment_types: equipments,
      ...getVisibilityBody(formState),
      email_notification_settings: formState.emailNotificationType,
      sharee_role_ids: getShareeRoleIdsForBody(),
      project: buildProjectBody(),
      supplier_shipment_info: buildSupplierShipmentInfo(),
    };
    return createShipmentRequestBody;
  };

  const onShipmentSubmitSuccess = () => {
    // call the callbacks - let parent decide scroll behavior
    if (onSubmitShipmentSuccessCallback) {
      onSubmitShipmentSuccessCallback();
    }
  };

  const handleReviewShipment = () => {
    setError(null);
    setReviewAttempted(true);
    // First run validations to show any errors
    if (!routePointsAreValid()) {
      return;
    }
    if (!ltlNotesAreValid()) {
      return;
    }
    if (!shipmentCommodityIsValid()) {
      return;
    }
    if (!bookNowIsValid()) {
      return;
    }
    if (!visibilitySelectionIsValid()) {
      return;
    }

    if (isNil(formState.weightLb)) {
      handleError("Weight must be a number.");
      return;
    }

    if (canUseBookNow && isInvalidNumber(formState.bookNowPrice)) {
      handleError("Book Now price must be a number.");
      return;
    }

    if (!optionalNumericFieldsAreValid()) {
      return;
    }

    if (
      formState.shipmentType === "MULTI_SHIPMENT_PROJECT" &&
      !availableLoadsAreValid(formState.availableLoads)
    ) {
      handleError(AVAILABLE_LOADS_ERROR_MESSAGE);
      return;
    }

    // If all validations pass, open the review modal
    setShowReviewModal(true);
  };

  const handleReviewModalClose = () => {
    setShowReviewModal(false);
  };

  const handleConfirmAndSubmit = () => {
    const shipment: CreateShipmentShipmentBody = {
      service: formState.service,
      type: formState.shipmentType,
      weight_lb: formState.weightLb,
      footage: parseNumericFormInput(formState.footage),
      preferred_price: parseNumericFormInput(formState.preferredPrice),
      currency: formState.currency,
      commodity: "",
      special_requirements: formState.specialRequirements,
      additional_details: formState.additionalDetails,
      commodity_freetext: formState.commodityFreetext,
      book_now_currency: canUseBookNow ? formState.bookNowCurrency : null,
      book_now_price: canUseBookNow ? formState.bookNowPrice : null,
      custom_name: formState.customName === "" ? null : formState.customName,
      quoting_unit_of_measure: formState.quotingUnitOfMeasure,
      analytics: {
        shipment_creation_mode: formState.analytics.shipmentCreationMode,
        shipment_creation_start:
          formState.analytics.creationStartingTime?.toISO() ??
          DateTime.now().toISO(),
        shipment_creation_end: DateTime.now().toISO(),
        file_attachments: formState.files.map((file) => file.name),
        encountered_errors: formState.analytics.encounteredErrors,
        route_point_address_modes: formState.analytics.routePointAddressModes,
      },
    };
    if (!routePointsAreValid()) {
      return;
    }
    if (!ltlNotesAreValid()) {
      return;
    }
    if (!shipmentCommodityIsValid()) {
      return;
    }
    if (!bookNowIsValid()) {
      return;
    }
    if (!directBookingIsValid()) {
      return;
    }
    const route = buildRequestBodyRoutePoints();
    const equipments = formState.equipmentTypes;

    const createShipmentRequestBody: CreateShipmentBody = {
      shipment,
      route,
      equipment_types: equipments,
      ...getVisibilityBody(formState),
      email_notification_settings: formState.emailNotificationType,
      sharee_role_ids: getShareeRoleIdsForBody(),
      project: buildProjectBody(),
      supplier_shipment_info: buildSupplierShipmentInfo(),
    };

    if (isNil(formState.weightLb)) {
      handleError("Weight must be a number.");
      return;
    }

    if (canUseBookNow && isInvalidNumber(formState.bookNowPrice)) {
      handleError("Book Now price must be a number.");
      return;
    }

    if (!optionalNumericFieldsAreValid()) {
      return;
    }

    if (
      formState.shipmentType === "MULTI_SHIPMENT_PROJECT" &&
      !availableLoadsAreValid(formState.availableLoads)
    ) {
      handleError(AVAILABLE_LOADS_ERROR_MESSAGE);
      return;
    }

    const shipmentBodyErrors = validate(
      createShipmentRequestBody,
      createShipmentRequestBodyConstraints,
    );

    if (!shipmentBodyErrors) {
      if (onPreSubmitShipment) {
        onPreSubmitShipment(createShipmentRequestBody);
      }
      if (submitShipment) {
        setSubmitLoading(true);
        api("post", postShipmentUrl, createShipmentRequestBody)
          .then(async (response) => {
            setError(null);
            let didFileUploadFail = false;

            posthog?.capture(ClientAppEvent.SHIPMENT_POSTED, {
              role,
              body: createShipmentRequestBody,
              is_direct_booking: assignment !== null,
            });

            // If there are files to upload, upload them now
            if (formState.files.length > 0 && user) {
              try {
                const shipmentId = (response.data as any)?.id;
                if (!shipmentId) {
                  throw new Error("Shipment created but no ID returned");
                }

                const uploadResults = await uploadFilesToS3(
                  formState.files,
                  `/shipments/${shipmentId}/create-shipment-detail-file-upload-url`,
                  user.id.toString(),
                );

                const failedUploads = uploadResults.filter(
                  (result) => !result.success,
                );
                didFileUploadFail = failedUploads.length > 0;

                posthog?.capture(ClientAppEvent.SHIPMENT_FILES_UPLOADED, {
                  role,
                  shipment_id: shipmentId,
                  file_count: formState.files.length,
                  upload_success: !didFileUploadFail,
                  failed_uploads_count: failedUploads.length,
                });
              } catch {
                didFileUploadFail = true;
              }
            }
            onOpenShipmentPostedDialog();
            resetFormState();
            setReviewAttempted(false);
            setShowReviewModal(false);
            onShipmentSubmitSuccess();
            if (didFileUploadFail) {
              const alertMsg =
                "Shipment created successfully, but failed to upload files. Please email your files to support@evotrux.com";
              handleError(alertMsg, true);
              // eslint-disable-next-line no-alert
              window.alert(alertMsg);
            }
          })
          .catch((e) => {
            const errorCode = e?.response?.data?.code;
            const apiErrorMessage = getApiErrorMessage(e, "");
            posthog?.capture(ClientAppEvent.SHIPMENT_POSTING_FAILED, {
              role,
              status: e?.response?.status,
              error_code: errorCode,
              error_message: redactEmails(apiErrorMessage),
              shipment_type: formState.shipmentType,
              notification_type: formState.notificationSettingsType,
              is_direct_booking: assignment !== null,
            });
            if (
              e?.response?.status === 403 &&
              errorCode === APIErrorCode.MISSING_POSTING_PERMISSION
            ) {
              setShowReviewModal(false);
              setShowMissingPostingPermissionModal(true);
            } else {
              handleError(
                getDirectBookingErrorMessage(errorCode, apiErrorMessage) ??
                  (apiErrorMessage ||
                    "Failed to create shipment. Please contact us if the problem persists."),
              );
            }
            setSubmitLoading(false);
          })
          .finally(() => {
            setSubmitLoading(false);
          });
      }
    }
    // display the first validation error message.
    /*
      Sample structure of validation errors:
      {
        "equipment_type": [
            "Equipment type Invalid Equipment Type"
        ],
      }
      */
    if (shipmentBodyErrors != null && shipmentBodyErrors !== undefined) {
      const errorKeys = Object.keys(shipmentBodyErrors);
      if (errorKeys.length > 0 && errorKeys[0].length > 0) {
        posthog?.capture(
          ClientAppEvent.SHIPMENT_POSTING_BLOCKED_BY_VALIDATION,
          {
            role,
            shipment_type: formState.shipmentType,
            invalid_fields: errorKeys,
          },
        );
        handleError(shipmentBodyErrors[errorKeys[0]][0]);
      }
    }
  };

  const moveRoutePoint = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setFormState((prev) => {
        if (
          dragIndex === 0 ||
          dragIndex === prev.routePoints.length - 1 ||
          hoverIndex === 0 ||
          hoverIndex === prev.routePoints.length - 1
        ) {
          return prev;
        }
        const prevPoints = [...prev.routePoints];
        const originalItem = prevPoints[dragIndex];
        prevPoints.splice(dragIndex, 1);
        prevPoints.splice(hoverIndex, 0, originalItem);
        const updatedAddressModes: RoutePointAddressModes = {};
        prevPoints.forEach((rp, index) => {
          updatedAddressModes[index] = rp.inputMode;
        });
        return {
          ...prev,
          routePoints: prevPoints,
          analytics: {
            ...prev.analytics,
            routePointAddressModes: updatedAddressModes,
          },
        };
      });
    },
    [setFormState],
  );

  const setShipmentNotificationType = (type: ShipmentNotificationType) => {
    handleValueChange("notificationSettingsType", type);
  };

  const setShipmentNotificationEmails = (emails: string[]) => {
    handleValueChange("notificationSetingsEmails", emails);
  };

  const setPartnerGroups = (groups: string[]) => {
    handleValueChange("partnerGroups", groups);
  };

  const setNetworkInviteEmails = (emails: string[]) => {
    handleValueChange("networkInviteEmails", emails);
  };

  // Selecting "Direct Booking" drops what the other radios hold: partner
  // selections and Book Now.
  const handleDirectBookingSelected = () => {
    posthog?.capture(ClientAppEvent.CREATE_SHIPMENT_DIRECT_BOOKING_SELECTED, {
      role,
      shipment_type: formState.shipmentType,
    });
    setError(null);
    setReviewAttempted(false);
    setFormState((prev) => ({
      ...prev,
      directBookingAssignment: {
        carrierEmail: "",
        carrierDisplayName: null,
        price: "",
        currency: DEFAULT_ASSIGNMENT_CURRENCY,
      },
      notificationSetingsEmails: [],
      partnerGroups: [],
      networkInviteEmails: [],
      bookNowPrice: "",
      isBookNow: false,
    }));
    logStartingTime();
  };

  const handleDirectBookingCleared = () => {
    handleValueChange<DirectBookingAssignmentFormState>(
      "directBookingAssignment",
      null,
    );
  };

  const handleDirectBookingAssignmentChange = (
    nextAssignment: DirectBookingAssignmentFormState,
  ) => {
    handleValueChange<DirectBookingAssignmentFormState>(
      "directBookingAssignment",
      nextAssignment,
    );
  };

  const setShareeRoleIds = (roleIds: number[]) => {
    setFormState((prev) => ({
      ...prev,
      shareeRoleIds: roleIds,
      shareShipment: roleIds.length > 0,
    }));
  };

  const handleCustomerSelectionChange = (data: {
    customerRoleIds: number[];
    externalCustomerIds: number[];
    customerInviteEmails: string[];
  }) => {
    handleValueChange("customerRoleIds", data.customerRoleIds);
    handleValueChange("externalCustomerIds", data.externalCustomerIds);
    handleValueChange("customerInviteEmails", data.customerInviteEmails);
  };

  const getShowBookNow = () => {
    if (formState.shipmentType === "QUOTES_ONLY" || assignment !== null) {
      return false;
    }
    return showBookNow ?? true;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Snackbar
        open={shipmentPostedPromptOpen}
        autoHideDuration={3000}
        onClose={onCloseShipmentPostedDialog}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={onCloseShipmentPostedDialog}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Shipment Created!
        </Alert>
      </Snackbar>
      <CreateShipmentForm
        equipmentTypes={formState.equipmentTypes}
        routePoints={formState.routePoints.map((rp, i) => (
          <RoutePoint
            key={rp.clientId}
            index={i}
            routePoint={rp}
            move={moveRoutePoint}
            onDeleteMidpoint={onDeleteMidRoutePoint}
            onSetRoutePointBeingEdited={onSetRoutePointBeingEdited}
            handleRoutePointValueChange={handleRoutePointValueChange}
            handleRoutePointLocationValueChange={
              handleRoutePointLocationValueChange
            }
            updateInputModeForRoutePoint={updateInputModeForRoutePoint}
            allowFlexible={
              assignment === null || i !== formState.routePoints.length - 1
            }
          />
        ))}
        serviceType={formState.service}
        weightLb={formState.weightLb}
        specialRequirements={formState.specialRequirements}
        additionalDetails={formState.additionalDetails}
        error={error}
        onValueChanged={handleValueChange}
        toggleCreateRouteOpen={handleOpen}
        onSubmit={handleReviewShipment}
        submitLoading={submitLoading}
        setShipmentNotificationType={setShipmentNotificationType}
        setShipmentNotificationEmails={setShipmentNotificationEmails}
        commodityFreetext={formState.commodityFreetext}
        shipmentNotificationType={formState.notificationSettingsType}
        selectedCarriers={formState.notificationSetingsEmails}
        createTemplateButton={
          showCreateTemplate ? (
            <CreateShipmentTemplateButton
              getShipmentBody={getShipmentBody}
              getTemplateError={() => getDirectBookingTemplateError(assignment)}
            />
          ) : (
            <Box />
          )
        }
        showBookNow={getShowBookNow()}
        bookNowPrice={formState.bookNowPrice}
        bookNowCurrency={formState.bookNowCurrency}
        customName={formState.customName}
        emailNotificationType={formState.emailNotificationType}
        showShareMenu={showShareMenu && !(assignment !== null && isFreeTier)}
        setShareeRoleIds={setShareeRoleIds}
        selectedSharees={formState.shareeRoleIds}
        shipmentType={formState.shipmentType}
        availableLoads={formState.availableLoads}
        files={formState.files}
        onFileErrors={(errors: string[]) =>
          errors.forEach((errorMessage) => handleError(errorMessage, true))
        }
        formState={formState}
        showReviewModal={showReviewModal}
        onReviewModalClose={handleReviewModalClose}
        onConfirmSubmit={handleConfirmAndSubmit}
        onCustomerSelectionChange={handleCustomerSelectionChange}
        supplierCustomers={supplierCustomers}
        quotingUnitOfMeasure={formState.quotingUnitOfMeasure}
        setPartnerGroups={setPartnerGroups}
        setNetworkInviteEmails={setNetworkInviteEmails}
        onEmailInputRef={setEmailInputRef}
        directBooking={{
          assignment,
          onAssignmentChange: handleDirectBookingAssignmentChange,
          assigneeCheck,
          reviewAttempted,
          disabledReason: getDirectBookingShipmentTypeError(
            formState.shipmentType,
          ),
          quotingUnitOfMeasure: formState.quotingUnitOfMeasure,
        }}
        onDirectBookingSelected={handleDirectBookingSelected}
        onDirectBookingCleared={handleDirectBookingCleared}
        quotesOnlyDisabledReason={
          assignment !== null ? DIRECT_BOOKING_MESSAGE.QUOTES_ONLY : null
        }
        equipmentError={
          assignment !== null
            ? getDirectBookingEquipmentError(formState.equipmentTypes)
            : null
        }
      />
      <RoutesFormModal
        open={isOpen}
        onClose={onClose}
        form={
          <RoutesForm
            onSubmit={(value) =>
              handleValueChange<Partial<RoutePoint>>("routePoints", value)
            }
          />
        }
      />
      <MissingPostingPermissionModal
        open={showMissingPostingPermissionModal}
        onClose={() => setShowMissingPostingPermissionModal(false)}
      />
    </DndProvider>
  );
}
